import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton } from '@mylighthouse/prism-ember';
import type { TaskForAsyncTaskFunction } from 'ember-concurrency';
import { t } from 'ember-intl';
import type { RecommendationStrategy } from 'lumen/models';
import type { RoomOffsetHierarchy } from 'lumen/models/recommendation-strategy';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { safeRollback } from 'lumen/utils/pricing-assistant/recommendation-settings';

import styles from './strategy-card.module.css';

function hierarchyContainsRoom(
  hierarchy: Record<string, RoomOffsetHierarchy>,
  roomCode: string,
): boolean {
  for (const [code, node] of Object.entries(hierarchy)) {
    if (code === roomCode || hierarchyContainsRoom(node.children, roomCode)) {
      return true;
    }
  }
  return false;
}

interface SettingsRecommendationsRoomHierarchyStrategyCardSignature {
  Args: {
    saveStrategyTask: TaskForAsyncTaskFunction<
      { drop: boolean },
      (
        strategyRecord: RecommendationStrategy,
      ) => Promise<{ isSuccess: boolean } | null>
    >;
    strategy: RecommendationStrategy;
    title: string;
  };
  Blocks: {
    default: [boolean, (hasError: boolean) => void];
  };
}

export default class SettingsRecommendationsRoomHierarchyStrategyCard extends Component<SettingsRecommendationsRoomHierarchyStrategyCardSignature> {
  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];

  @tracked hasBlockingOffsetError = false;
  @tracked isEditMode = false;

  get baseRoomCode(): string {
    return this.hotelAndMarketService.hotel.pricingIntegration
      .pms_leading_room_pms_id;
  }

  get isSaveButtonDisabled(): boolean {
    return (
      this.args.saveStrategyTask.isRunning ||
      !this.args.strategy.hasDirtyAttributes
    );
  }

  willDestroy(): void {
    super.willDestroy();

    safeRollback(this.args.strategy);
  }

  @action
  disableEditMode(): void {
    this.isEditMode = false;
  }

  @action
  enableEditMode(): void {
    this.isEditMode = true;
  }

  @action
  onCancelClick(): void {
    this.args.strategy.rollbackAttributes();

    this.disableEditMode();
  }

  @action
  async onSaveClick(): Promise<void> {
    if (
      !this.args.strategy ||
      this.hasBlockingOffsetError ||
      this.args.strategy.isStrategyInvalid
    ) {
      return;
    }

    /* A hierarchy without the base room is invalid — block save and warn the user. */
    const { settings } = this.args.strategy;

    if (
      'hierarchy' in settings &&
      !hierarchyContainsRoom(settings.hierarchy, this.baseRoomCode)
    ) {
      await this.alerting.showWarningAlert({
        title: this.intl.t(
          'pricing_assistant.recommendations.settings.room_hierarchy.base_room_missing_title',
        ),
        text: this.intl.t(
          'pricing_assistant.recommendations.settings.room_hierarchy.base_room_missing_description',
        ),
        buttons: {
          cancel: {
            text: this.intl.t('general.got_it'),
          },
        },
      });
      return;
    }

    const result = await this.args.saveStrategyTask.perform(this.args.strategy);

    if (!result?.isSuccess) {
      return;
    }

    this.disableEditMode();
  }

  @action
  setHasBlockingOffsetError(hasError: boolean): void {
    this.hasBlockingOffsetError = hasError;
  }

  <template>
    <div class={{styles.card}}>
      {{#if this.isEditMode}}
        <div class="{{styles.card-header-wrapper}} items-center gap-200">
          <h3>{{@title}}</h3>
          <div class="flex flex-row gap-200">
            <PrismButton
              @buttonType="secondary"
              @isDisabled={{@saveStrategyTask.isRunning}}
              @label={{t "general.cancel"}}
              @onClick={{this.onCancelClick}}
              data-test-button="Cancel"
            />
            <PrismButton
              @isDisabled={{this.isSaveButtonDisabled}}
              @isLoading={{@saveStrategyTask.isRunning}}
              @label={{t "general.save"}}
              @onClick={{this.onSaveClick}}
              data-test-button="Save"
            />
          </div>
        </div>
      {{else}}
        <div class="{{styles.card-header-wrapper}} items-center gap-200">
          <h3>{{@title}}</h3>
          {{#unless
            this.access.isPricingAssistantRecommendationSettingsActionOrInputDisabled
          }}
            <PrismButton
              @buttonType="ghost"
              @icon="edit-pencil"
              @onClick={{this.enableEditMode}}
              data-test-button="Enable edit mode"
            />
          {{/unless}}
        </div>
      {{/if}}
      <div class="flex-1 pr-400 pb-400 pl-400">
        {{yield this.isEditMode this.setHasBlockingOffsetError}}
      </div>
    </div>
  </template>
}
